import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps the "il ne reste que X pièces" stock text in sync with whichever
 * variant (color + size) is currently selected - see stock-progress.liquid's
 * doc comment for the full picture. Bug this fixes: the block used to be a
 * plain server-rendered <div> with no JS at all, so it only ever reflected
 * whichever variant the page happened to load on and never updated again -
 * switching color/size left the first-loaded variant's stock number on
 * screen regardless of what was actually selected.
 *
 * Fix follows this theme's own established pattern for this exact problem
 * (see assets/product-inventory.js, the native equivalent for the
 * in-stock/low-stock/out-of-stock badge): listen for `shopify:product:select`
 * - the event assets/variant-picker.js already dispatches on every
 * color/size change, whether or not this component does anything with it -
 * on the nearest `.shopify-section` ancestor (not on this element itself:
 * the event is dispatched on <variant-picker>, a sibling, not a parent of
 * this block, so listening here needs a common ancestor). Once the fetch
 * behind that event resolves, morph this element to match the same
 * `<stock-progress>` tag found in the freshly-fetched HTML for the newly
 * selected variant - true server-computed data for that exact variant, not
 * a client-side guess.
 *
 * The root tag is now unconditionally rendered by the Liquid (see its doc
 * comment for why), including when there's nothing to show - so a plain
 * `morph(this, newStockProgress)` handles the out-of-stock-variant case
 * for free: the fetched tag is just empty in that case, and morphing to an
 * empty element naturally clears this one's stale content instead of
 * leaving a wrong number on screen.
 */
class StockProgress extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    // Ignore variant-select events from unrelated pickers elsewhere on the
    // page (e.g. a product-recommendations card) - same guard
    // product-inventory.js uses for the same reason.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const newStockProgress = detail.html.querySelector('stock-progress');
        if (!newStockProgress) return;
        morph(this, newStockProgress, { childrenOnly: false });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[stock-progress] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('stock-progress')) {
  customElements.define('stock-progress', StockProgress);
}
